import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useAction } from 'wasp/client/operations';
import {
  getOrganizationById,
  deleteOrganization,
  getOrganizations,
  getServiceDefinitions
} from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import {
  Building2,
  Clock,
  MapPin,
  CheckCircle,
  XCircle,
  Layers,
  GitBranch,
  Users,
  ChevronUp,
  ChevronDown,
  Mail,
  Phone,
  Globe,
  ExternalLink
} from 'lucide-react';
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButtonLink,
  ActionButtonDanger
} from '../../components/layout';
import { toast } from '../../hooks/use-toast';
import { SocialLink } from './organizationTypes';

export default function ViewOrganizationPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const deleteOrganizationFn = useAction(deleteOrganization);

  const { data: organization, isLoading, error } = useQuery(getOrganizationById, { id: organizationId! });
  const { data: allOrganizations = [] } = useQuery(getOrganizations);
  const { data: allServiceDefinitions = [] } = useQuery(getServiceDefinitions, {});

  const formatDate = (dateInput: string | Date) => {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const currentIndex = (allOrganizations as any[]).findIndex((org: any) => org.id === organizationId);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < (allOrganizations as any[]).length - 1;
  const prevOrganization = hasPrev ? (allOrganizations as any[])[currentIndex - 1] : null;
  const nextOrganization = hasNext ? (allOrganizations as any[])[currentIndex + 1] : null;

  const handleDelete = async () => {
    if (!organization) return;
    if (!confirm(`Are you sure you want to delete "${organization.name}"?`)) return;

    try {
      await deleteOrganizationFn({ id: organization.id });
      toast({ title: 'Success', description: 'Organization deleted successfully' });
      navigate('/admin/organizations');
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to delete organization',
        variant: 'destructive'
      });
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in to view organizations.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization...</p>
        </div>
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Building2 className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Organization Not Found</h3>
          <p className="text-gray-600 mb-4">The organization you&apos;re looking for doesn&apos;t exist or has been removed.</p>
          <button
            onClick={() => navigate('/admin/organizations')}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Organizations
          </button>
        </div>
      </div>
    );
  }

  const settings = organization.settings as any;
  const socialLinks: SocialLink[] = settings?.social || [];
  const serviceDefinitionIds: string[] = settings?.services?.definitionIds || [];
  const linkedServices = (allServiceDefinitions as any[]).filter((d: any) =>
    serviceDefinitionIds.includes(d.id)
  );

  const orgEmail = (organization as any).email as string | undefined;
  const orgPhone = (organization as any).phone as string | undefined;
  const orgContact = (organization as any).contact as string | undefined;
  const hasContactInfo = !!(orgContact || orgEmail || orgPhone || organization.website);

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <div className="space-y-3">
          <div className="flex gap-2">
            <button
              onClick={() => prevOrganization && navigate(`/admin/organizations/${prevOrganization.id}`)}
              disabled={!hasPrev}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              title={hasPrev ? `Previous: ${prevOrganization?.name}` : 'No previous item'}
            >
              <ChevronUp className="w-4 h-4" />
              <span className="text-sm font-medium">Up</span>
            </button>
            <button
              onClick={() => nextOrganization && navigate(`/admin/organizations/${nextOrganization.id}`)}
              disabled={!hasNext}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              title={hasNext ? `Next: ${nextOrganization?.name}` : 'No next item'}
            >
              <span className="text-sm font-medium">Down</span>
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>

          <ActionButtons>
            <ActionButtonLink to={`/admin/organizations/${organization.id}/edit`}>
              Edit
            </ActionButtonLink>
            <ActionButtonDanger onClick={handleDelete}>
              Delete
            </ActionButtonDanger>
          </ActionButtons>
        </div>
      </SidebarSection>

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField label="Organization Type" value={organization.organizationType?.label || 'N/A'} />
          <DetailField label="Status" value={organization.isActive ? 'Active' : 'Inactive'} />
          <DetailField label="Created" value={formatDate(organization.createdAt)} />
          <DetailField label="Last Updated" value={formatDate(organization.updatedAt)} />
          <DetailField label="Sub-Organizations" value={organization._count?.subOrganizations || 0} />
          <DetailField label="Locations" value={organization._count?.locationOrganizations || 0} />
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={[
        { label: 'Admin', href: '/admin' },
        { label: 'Organizations', href: '/admin/organizations', isClickable: true }
      ]}
      currentPageUrl={typeof window !== 'undefined' ? window.location.href : ''}
      sidebarContent={sidebarContent}
      layoutVariant="taxonomy"
    >
      {/* Organization Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{organization.name}</h1>
            </div>
            <p className="text-lg text-gray-600 font-mono mb-4">{organization.slug}</p>
          </div>

          <div className="flex items-center gap-2 ml-4">
            {organization.isActive ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-green-100 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Active</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-red-100 text-red-800">
                <XCircle className="w-4 h-4" />
                <span className="font-medium">Inactive</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 border-b border-gray-200 pb-6">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Created {formatDate(organization.createdAt)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Updated {formatDate(organization.updatedAt)}</span>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Type */}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Building2 className="w-4 h-4" />
          <span>{organization.organizationType?.label || 'N/A'}</span>
        </div>

        {/* Description */}
        {organization.description && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Description</h3>
            <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
              {organization.description}
            </p>
          </div>
        )}

        {/* Contact */}
        {hasContactInfo && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact</h3>
            <div className="space-y-3 text-sm">
              {orgContact && (
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-900">{orgContact}</span>
                </div>
              )}
              {orgEmail && (
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <a
                    href={`mailto:${orgEmail}`}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    {orgEmail}
                  </a>
                </div>
              )}
              {orgPhone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <a
                    href={`tel:${orgPhone}`}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    {orgPhone}
                  </a>
                </div>
              )}
              {organization.website && (
                <div className="flex items-center gap-3">
                  <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <a
                    href={organization.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
                  >
                    {organization.website}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Social Media */}
        {socialLinks.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Social Media</h3>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((link, i) => (
                <a
                  key={i}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300 text-sm font-medium transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Services */}
        {linkedServices.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Services Offered</h3>
            <div className="flex flex-wrap gap-2">
              {linkedServices.map((def: any) => (
                <span
                  key={def.id}
                  className="inline-flex items-center px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-sm font-medium"
                >
                  {def.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Quick Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Info</h3>
          <div className="space-y-3 text-sm">
            {(organization as any).oid && (
              <div className="flex items-start gap-3">
                <Building2 className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-600">OID</p>
                  <p className="font-medium text-gray-900">{(organization as any).oid}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Building2 className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-600">Organization Type</p>
                <p className="font-medium text-gray-900">{organization.organizationType?.label || 'N/A'}</p>
              </div>
            </div>

            {(organization as any).specialization && (
              <div className="flex items-start gap-3">
                <Layers className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-600">Specialization</p>
                  <p className="font-medium text-gray-900">{(organization as any).specialization}</p>
                </div>
              </div>
            )}

            {(organization as any).scope && (
              <div className="flex items-start gap-3">
                <Globe className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-600">Organization Scope</p>
                  <p className="font-medium text-gray-900">{(organization as any).scope}</p>
                </div>
              </div>
            )}

            {Array.isArray((organization as any).languages) && (organization as any).languages.length > 0 && (
              <div className="flex items-start gap-3">
                <Globe className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-600">Languages</p>
                  <p className="font-medium text-gray-900">{(organization as any).languages.join(', ')}</p>
                </div>
              </div>
            )}

            {organization.parentOrganization && (
              <div className="flex items-start gap-3">
                <GitBranch className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-600">Parent Organization</p>
                  <Link
                    to={`/admin/organizations/${organization.parentOrganization.id}`}
                    className="font-medium text-blue-600 hover:text-blue-800"
                  >
                    {organization.parentOrganization.name}
                  </Link>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Users className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-600">Sub-Organizations</p>
                <p className="font-medium text-gray-900">{organization._count?.subOrganizations || 0}</p>
              </div>
            </div>

            {(organization as any).isNational && (
              <div className="flex items-start gap-3">
                <Globe className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-600">Scope</p>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium">
                    National
                  </span>
                </div>
              </div>
            )}

            {(organization.cityName || organization.provinceCode || (organization as any).suburbName || (organization as any).neighbourhoodName) && (
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-600">Location</p>
                  <p className="font-medium text-gray-900">
                    {[
                      (organization as any).neighbourhoodName,
                      (organization as any).suburbName,
                      organization.cityName,
                      organization.provinceCode
                    ].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>
            )}

            {(organization as any).address && (
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-600">Address</p>
                  <p className="font-medium text-gray-900 whitespace-pre-wrap">{(organization as any).address}</p>
                </div>
              </div>
            )}

            {(organization as any).postalCode && (
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-600">Postal Code</p>
                  <p className="font-medium text-gray-900">{(organization as any).postalCode}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-600">Linked Locations</p>
                <p className="font-medium text-gray-900">{organization._count?.locationOrganizations || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sub-Organizations List */}
        {organization.subOrganizations && organization.subOrganizations.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <GitBranch className="w-5 h-5" />
              Sub-Organizations
            </h3>
            <div className="space-y-2">
              {organization.subOrganizations.map((subOrg: any) => (
                <Link
                  key={subOrg.id}
                  to={`/admin/organizations/${subOrg.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">{subOrg.name}</p>
                    <p className="text-sm text-gray-500">{subOrg.slug}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Linked Locations */}
        {organization.locationOrganizations && organization.locationOrganizations.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Linked Locations
            </h3>
            <div className="space-y-2">
              {organization.locationOrganizations.map((locOrg: any) => (
                <div key={locOrg.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">{locOrg.location?.name || 'Unknown Location'}</p>
                    <p className="text-sm text-gray-500">{locOrg.location?.locationCode || ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </IndividualViewPageLayout>
  );
}



